import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, SelectField, Spinner, TextField } from '../../components/ui';
import { formatDate } from './dates';
import {
  CreditNoteCreate,
  DraftLineAdd,
  Invoice,
  PAYMENT_METHODS,
  PLAT,
  Payment,
  PaymentRecord,
  formatAllowance,
  formatUsage,
  invoiceStatusLabel,
  invoiceStatusTone,
  keys,
  metricLabel,
  paymentMethodLabel,
  penceToInput,
  vatPercent,
} from './api';
import { Drawer, Money, MoneyField } from './parts';

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['billing'] });
}

function AddLineCard({ id }: { id: string }) {
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unit, setUnit] = useState<number | null>(0);
  const [round, setRound] = useState(0);
  const refresh = useRefresh();
  const toast = useToast();
  const add = useMutation({
    mutationFn: () => api.post(`${PLAT}/invoices/${id}/lines`, { body: DraftLineAdd.parse({ description, quantity: Number(quantity), unitPrice: unit }), schema: Invoice }),
    onSuccess: async () => {
      toast(t('billingUi.invoice.lineAdded'));
      setDescription('');
      setRound((r) => r + 1);
      await refresh();
    },
  });
  const ok = description.trim() !== '' && unit !== null && Number(quantity) >= 0 && quantity !== '';
  return (
    <Card title={t('billingUi.invoice.addLine')} description={t('billingUi.invoice.addLineHelp')}>
      <ErrorLine error={add.error} />
      <div className="grid gap-3 sm:grid-cols-3">
        <TextField className="sm:col-span-3" label={t('billingUi.invoice.lineDescription')} value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} />
        <TextField label={t('billingUi.invoice.quantity')} type="number" min={0} step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        <MoneyField key={round} label={t('billingUi.invoice.unitPrice')} hint={t('billingUi.invoice.unitHint')} value={unit} allowNegative onValue={setUnit} />
        <div className="flex items-end">
          <Button variant="primary" disabled={!ok || add.isPending} onClick={() => add.mutate()}>
            {t('billingUi.invoice.addLineButton')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PaymentCard({ inv }: { inv: Invoice }) {
  const [amount, setAmount] = useState<number | null>(inv.balance > 0 ? inv.balance : null);
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState<(typeof PAYMENT_METHODS)[number]>('bank_transfer');
  const [reference, setReference] = useState('');
  const refresh = useRefresh();
  const toast = useToast();
  const pay = useMutation({
    mutationFn: () => api.post(`${PLAT}/invoices/${inv.id}/payments`, { body: PaymentRecord.parse({ amount, paidOn, method, reference }), schema: Payment }),
    onSuccess: async () => {
      toast(t('billingUi.invoice.paymentRecorded'));
      await refresh();
    },
  });
  return (
    <Card title={t('billingUi.invoice.recordPayment')} description={t('billingUi.invoice.recordPaymentHelp')}>
      <ErrorLine error={pay.error} />
      <div className="grid gap-3 sm:grid-cols-2">
        <MoneyField label={t('billingUi.invoice.amount')} value={amount} onValue={setAmount} />
        <TextField label={t('billingUi.invoice.paidOn')} type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        <SelectField label={t('billingUi.invoice.method')} value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {paymentMethodLabel(m)}
            </option>
          ))}
        </SelectField>
        <TextField label={t('billingUi.invoice.reference')} value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} />
      </div>
      <div className="mt-3">
        <Button variant="primary" disabled={amount === null || amount < 1 || !paidOn || pay.isPending} onClick={() => pay.mutate()}>
          {t('billingUi.invoice.recordPaymentButton')}
        </Button>
      </div>
    </Card>
  );
}

function CreditCard({ inv }: { inv: Invoice }) {
  const [reason, setReason] = useState('');
  const [full, setFull] = useState(true);
  const [lines, setLines] = useState<{ description: string; amount: number | null }[]>([{ description: '', amount: null }]);
  const refresh = useRefresh();
  const toast = useToast();
  const credit = useMutation({
    mutationFn: () =>
      api.post(`${PLAT}/credit-notes`, {
        body: CreditNoteCreate.parse({
          invoiceId: inv.id,
          reason,
          full,
          lines: full ? [] : lines.filter((l) => l.description.trim() && l.amount).map((l) => ({ description: l.description, amount: l.amount })),
        }),
        schema: Invoice,
      }),
    onSuccess: async () => {
      toast(t('billingUi.invoice.creditIssued'));
      setReason('');
      await refresh();
    },
  });
  const linesOk = lines.some((l) => l.description.trim() && l.amount);
  return (
    <Card title={t('billingUi.invoice.creditTitle')} description={t('billingUi.invoice.creditHelp')}>
      <ErrorLine error={credit.error} />
      <div className="space-y-3">
        <TextField label={t('billingUi.invoice.creditReason')} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
        <CheckboxField label={t('billingUi.invoice.creditFull')} checked={full} onChange={(e) => setFull(e.target.checked)} />
        {full
          ? null
          : lines.map((l, i) => (
              <div key={i} className="grid items-end gap-3 sm:grid-cols-[2fr_1fr_auto]">
                <TextField label={t('billingUi.invoice.lineDescription')} value={l.description} maxLength={200} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
                <MoneyField label={t('billingUi.invoice.creditAmount')} value={l.amount} onValue={(p) => setLines(lines.map((x, j) => (j === i ? { ...x, amount: p } : x)))} />
                <Button onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1} aria-label={t('billingUi.invoice.removeCreditLine', { n: i + 1 })}>
                  {t('billingUi.remove')}
                </Button>
              </div>
            ))}
        {full ? null : <Button onClick={() => setLines([...lines, { description: '', amount: null }])}>{t('billingUi.invoice.addCreditLine')}</Button>}
        <ConfirmButton
          label={t('billingUi.invoice.creditButton')}
          confirmLabel={t('billingUi.invoice.creditConfirm')}
          disabled={reason.trim() === '' || (!full && !linesOk)}
          pending={credit.isPending}
          onConfirm={() => credit.mutate()}
        />
      </div>
    </Card>
  );
}

function Totals({ inv }: { inv: Invoice }) {
  const row = (label: string, pence: number, strong = false) => (
    <div className={`flex justify-between py-1 text-sm ${strong ? 'font-semibold' : ''}`}>
      <dt>{label}</dt>
      <dd>
        <Money pence={pence} />
      </dd>
    </div>
  );
  return (
    <dl className="ml-auto w-full max-w-xs border-t border-slate-200 pt-2">
      {row(t('billingUi.invoice.subtotal'), inv.subtotal)}
      {row(t('billingUi.invoice.vat', { rate: vatPercent(inv.vatRateBp) }), inv.vat)}
      {row(t('billingUi.invoice.total'), inv.total, true)}
      {inv.amountPaid ? row(t('billingUi.invoice.paid'), -inv.amountPaid) : null}
      {inv.amountCredited ? row(t('billingUi.invoice.credited'), -inv.amountCredited) : null}
      {row(t('billingUi.invoice.balance'), inv.balance, true)}
    </dl>
  );
}

/** One invoice or credit note. Drafts can be reviewed, topped up with lines, issued or voided; issued ones are read-only (credit only). */
export function InvoiceDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const canManage = useCan('platform.billing_manage');
  const refresh = useRefresh();
  const toast = useToast();
  const q = useQuery({ queryKey: keys.invoice(id), queryFn: () => api.get(`${PLAT}/invoices/${id}`, { schema: Invoice }) });
  const issue = useMutation({
    mutationFn: () => api.post(`${PLAT}/invoices/${id}/issue`, { schema: Invoice }),
    onSuccess: async (r) => {
      toast(t('billingUi.invoice.issuedToast', { number: r.number ?? '' }));
      await refresh();
    },
  });
  const voidDraft = useMutation({
    mutationFn: () => api.delete(`${PLAT}/invoices/${id}`),
    onSuccess: async () => {
      toast(t('billingUi.invoice.voided'));
      await refresh();
    },
  });
  const inv = q.data;
  const title = inv ? (inv.number ? t(inv.kind === 'credit_note' ? 'billingUi.invoice.creditNoteTitle' : 'billingUi.invoice.title', { number: inv.number }) : t('billingUi.invoice.draftTitle')) : t('billingUi.tabs.invoices');
  return (
    <Drawer title={title} onClose={onClose}>
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      {inv ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge tone={invoiceStatusTone(inv.status)}>{invoiceStatusLabel(inv.status)}</Badge>
            <span>{inv.tenantName}</span>
            {inv.periodStart && inv.periodEnd ? <span>{t('billingUi.sub.range', { from: formatDate(inv.periodStart), to: formatDate(inv.periodEnd) })}</span> : null}
            {inv.issuedOn ? <span>{t('billingUi.invoice.issuedOn', { date: formatDate(inv.issuedOn) })}</span> : null}
            {inv.dueOn ? <span>{t('billingUi.invoice.dueOn', { date: formatDate(inv.dueOn) })}</span> : null}
            {inv.pdfAvailable ? (
              <a
                className="font-medium text-brand hover:underline"
                href={`/api/v1${PLAT}/invoices/${inv.id}/pdf`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('billingUi.tenant.downloadPdf', { number: inv.number ?? '' })}
              >
                {t('billingUi.tenant.pdf')}
              </a>
            ) : null}
          </div>
          {inv.status === 'draft' ? <Alert kind="info">{t('billingUi.invoice.draftNote')}</Alert> : null}
          {inv.status !== 'draft' && inv.status !== 'void_draft' ? <p className="text-sm text-slate-600">{t('billingUi.invoice.readOnly')}</p> : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[28rem] text-sm">
              <caption className="sr-only">{t('billingUi.invoice.linesTable')}</caption>
              <thead className="border-b border-slate-200 text-left text-xs text-slate-600">
                <tr>
                  <th scope="col" className="py-2 pr-2">{t('billingUi.invoice.lineDescription')}</th>
                  <th scope="col" className="py-2 pr-2 text-right">{t('billingUi.invoice.quantity')}</th>
                  <th scope="col" className="py-2 pr-2 text-right">{t('billingUi.invoice.unitPrice')}</th>
                  <th scope="col" className="py-2 text-right">{t('billingUi.invoice.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {inv.lines.map((l, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-2">{l.description}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{l.quantity}</td>
                    <td className="py-2 pr-2 text-right"><Money pence={l.unitPrice} /></td>
                    <td className="py-2 text-right"><Money pence={l.amount} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Totals inv={inv} />
          {inv.usage.length > 0 ? (
            <div className="overflow-x-auto">
              <h3 className="mb-1 text-sm font-semibold">{t('billingUi.invoice.usage')}</h3>
              <table className="w-full min-w-[24rem] text-sm">
                <caption className="sr-only">{t('billingUi.invoice.usageTable')}</caption>
                <thead className="border-b border-slate-200 text-left text-xs text-slate-600">
                  <tr>
                    <th scope="col" className="py-2 pr-2">{t('billingUi.usage.metric')}</th>
                    <th scope="col" className="py-2 pr-2 text-right">{t('billingUi.usage.used')}</th>
                    <th scope="col" className="py-2 pr-2 text-right">{t('billingUi.usage.allowance')}</th>
                    <th scope="col" className="py-2 text-right">{t('billingUi.usage.charged')}</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.usage.map((u) => (
                    <tr key={u.metric} className="border-b border-slate-100">
                      <td className="py-2 pr-2">{metricLabel(u.metric)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{formatUsage(u.metric, u.used)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{formatAllowance(u.metric, u.allowance)}</td>
                      <td className="py-2 text-right tabular-nums">{u.charged}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <p className="text-sm text-slate-600">
            {t('billingUi.invoice.billTo', { name: inv.contact.name || inv.contact.organisation || inv.tenantName, email: inv.contact.email })}
            {inv.contact.purchaseOrder ? ` ${t('billingUi.invoice.po', { po: inv.contact.purchaseOrder })}` : ''}
          </p>
          <ErrorLine error={issue.error ?? voidDraft.error} />
          {canManage && inv.status === 'draft' ? (
            <>
              <AddLineCard id={inv.id} />
              <div className="flex flex-wrap gap-3">
                <ConfirmButton label={t('billingUi.invoice.issue')} confirmLabel={t('billingUi.invoice.issueConfirm')} pending={issue.isPending} onConfirm={() => issue.mutate()} />
                <ConfirmButton label={t('billingUi.invoice.void')} confirmLabel={t('billingUi.invoice.voidConfirm')} pending={voidDraft.isPending} onConfirm={() => voidDraft.mutate()} />
              </div>
            </>
          ) : null}
          {canManage && inv.kind === 'invoice' && inv.status === 'issued' ? <PaymentCard key={`${inv.amountPaid}-${penceToInput(inv.balance)}`} inv={inv} /> : null}
          {canManage && inv.kind === 'invoice' && (inv.status === 'issued' || inv.status === 'paid') ? <CreditCard inv={inv} /> : null}
        </div>
      ) : null}
    </Drawer>
  );
}
